import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';
import { Modal, message, Button, Form } from 'antd';
import { OpenAIOutlined } from '@ant-design/icons';
import { routePaths } from 'router/route-paths.ts';
import { useGetCountries } from 'api/countries/use-get-countries';
import { useCreateOrderWithCargos } from 'api/orders/use-create-order-with-cargos.ts';
import type { TCreateOrderWithCargosReq, TOpenAiParserOrderRes } from 'api/swagger';
import { UserRole, withRoleGuard } from 'shared/hocs/with-role-guard.tsx';
import { formatDate } from 'shared/utils/time/format-date';
import { CargoType } from 'types/cargo';
import { OrderFromWithCargoAiScreen } from 'components/cargos/cargo-create-with-order/components/order-from-with-cargo-ai-screen';
import { transformAiResultCargosToFromObject } from 'components/cargos/cargo-create-with-order/utils';
import { CargoForm } from 'components/cargos/cargo-form';
import type { ICargoFormFields } from 'components/cargos/cargo-form';
import { prepareCargoAddressPayload, prepareOptionalCargoAddressPayload } from 'components/cargos/cargo-form/utils';
import { FormDrawer } from 'components/common/form-drawer';
import { EFormDrawerSize } from 'components/common/form-drawer-with-button';
import type { IOrderFormFields } from 'components/orders/order-form';
import { OrderFormWithCargoList } from './components/order-form-with-cargo-list.tsx';
import type { ICargoFormWithId } from './types.ts';

export const CargoCreateWithOrderComponent = () => {
  const [isOpenCargoForm, setIsOpenCargoForm] = useState(false);
  const [isOpenOrderForm, setIsOpenOrderForm] = useState(false);
  const [isAiStep, setIsAiStep] = useState(true);

  const [tmpS3Key, setTmpS3Key] = useState<string>('');
  const [cargos, setCargos] = useState<ICargoFormWithId[]>([]);
  const [editingCargoId, setEditingCargoId] = useState<string | null>(null);

  const { t } = useTranslation();

  const navigate = useNavigate();

  const [orderForm] = Form.useForm();

  const { data: countries } = useGetCountries();
  const { mutateAsync: createOrderWithCargos, isLoading } = useCreateOrderWithCargos();

  const onDeleteCargo = (cargoId: string) => {
    setCargos((prev) => prev.filter((cargo) => cargo.id !== cargoId));
    message.success(t('cargo:cargo_deleted'));
  };

  const onEditCargo = (cargoId: string) => {
    setEditingCargoId(cargoId);
    setIsOpenCargoForm(true);
  };

  const handleOpenCargoForm = () => {
    setEditingCargoId(null);
    setIsOpenCargoForm(true);
  };

  const handleCloseCargoForm = () => {
    setIsOpenCargoForm(false);
    setEditingCargoId(null);
  };

  const handleOpenOrderForm = () => setIsOpenOrderForm(true);

  const handleCloseOrderForm = () => {
    setIsOpenOrderForm(false);
    setIsAiStep(true);
    setCargos([]);
  };

  const handleCargoFinish = async (values: ICargoFormFields) => {
    const cargoWithId: ICargoFormWithId = {
      ...values,
      id: editingCargoId || crypto.randomUUID(),
      isCompleted: true,
    };

    if (editingCargoId) {
      setCargos((prev) => prev.map((cargo) => (cargo.id === editingCargoId ? cargoWithId : cargo)));
      message.success(t('cargo:cargo_updated'));
    } else {
      setCargos((prev) => [...prev, cargoWithId]);
      message.success(t('cargo:cargo_added'));
    }

    handleCloseCargoForm();
  };

  const finisAiStep = (result: TOpenAiParserOrderRes | null) => {
    setIsAiStep(false);

    if (!result) {
      return;
    }

    const newOrderFields = {
      freight: result.order.freight,
      currency: result.order.currency,
      clientOrderNumber: result.order.clientOrderNumber,
      ...(result.client
        ? { client: { value: result.client.id, label: result.client.shortName || result.client.name } }
        : {}),
      ...(result.contact ? { contact: { value: result.contact.id, label: result.contact.name } } : {}),
    };

    orderForm.setFieldsValue(newOrderFields);

    if (result.order.cargos) {
      setCargos(transformAiResultCargosToFromObject(result.order.cargos, countries));
    }

    setTmpS3Key(result.s3Key);
  };

  const handleFinish = async (values: IOrderFormFields) => {
    if (cargos.length === 0) {
      Modal.confirm({
        title: t('cargo:no_cargos_added'),
        content: t('cargo:no_cargos_content'),
        okText: t('cargo:add_cargo'),
        cancelText: t('common:cancel'),
        onOk: () => {
          handleOpenCargoForm();
        },
      });
      return;
    }

    const cargoIsNotCompleted = cargos.some((cargo) => !cargo.isCompleted);

    if (cargoIsNotCompleted) {
      Modal.confirm({
        title: t('cargo:cargos_is_not_competed'),
        content: t('cargo:cargos_is_not_competed_content'),
        cancelText: t('common:cancel'),
      });
      return;
    }

    const formattedCargos = cargos.map((cargo) => {
      const isContainerCargoEnabled = cargo.type === CargoType.CONTAINER;

      return {
        ...cargo,
        loadingAddress: prepareCargoAddressPayload(cargo.loadingAddress),
        unloadingAddress: prepareCargoAddressPayload(cargo.unloadingAddress),
        loadingCustomAddress: prepareOptionalCargoAddressPayload(cargo.loadingCustomAddress),
        unloadingCustomAddress: prepareOptionalCargoAddressPayload(cargo.unloadingCustomAddress),
        assetReturnAddress:
          cargo.type === CargoType.CONTAINER ? prepareOptionalCargoAddressPayload(cargo.assetReturnAddress) : undefined,
        cargoItems: isContainerCargoEnabled ? undefined : cargo.cargoItems,
        specificCargoItem: isContainerCargoEnabled ? cargo.specificCargoItem : undefined,
        loadingDateFrom: cargo.loadingDate?.[0] && formatDate(cargo.loadingDate?.[0]),
        loadingDateTo: cargo.loadingDate?.[1] && formatDate(cargo.loadingDate?.[1]),
        unloadingDateFrom: cargo.unloadingDate?.[0] && formatDate(cargo.unloadingDate?.[0]),
        unloadingDateTo: cargo.unloadingDate?.[1] && formatDate(cargo.unloadingDate?.[1]),
      };
    });

    const apiVariables: TCreateOrderWithCargosReq = {
      clientId: values.client.value,
      contactId: values.contact.value,
      clientOrderNumber: values.clientOrderNumber,
      freight: values.freight,
      currency: values.currency,
      documentPrice: values.documentPrice,
      paymentTerms: values.paymentTerms,
      paymentPeriod: values.paymentPeriod,
      cargos: formattedCargos,
      tmpS3Key,
    };
    const order = await createOrderWithCargos(apiVariables);

    navigate(generatePath(routePaths.order, { orderId: String(order.id) }));
  };

  const editingCargo = editingCargoId ? cargos.find((cargo) => cargo.id === editingCargoId) : undefined;

  return (
    <>
      <Button type='primary' icon={<OpenAIOutlined />} onClick={handleOpenOrderForm}>
        Add Order
      </Button>

      <OrderFromWithCargoAiScreen isOpen={isOpenOrderForm} onFinis={finisAiStep} onClose={handleCloseOrderForm} />

      <FormDrawer
        formComponent={OrderFormWithCargoList}
        title={t('order:create_order_with_cargos')}
        formProps={{ handleOpenCargoForm, cargos, onDeleteCargo, onEditCargo }}
        formInstance={orderForm}
        isOpen={isOpenOrderForm && !isAiStep}
        isLoading={isLoading}
        size={EFormDrawerSize.DEFAULT}
        displayMaskClickConfirmation
        preventCloseOnFinish
        onFinish={handleFinish}
        onClose={handleCloseOrderForm}
      />

      {isOpenCargoForm && (
        <FormDrawer
          formComponent={CargoForm}
          title={editingCargoId ? t('cargo:edit_cargo') : t('cargo:create_cargo')}
          formProps={editingCargo ? { initialValues: editingCargo } : undefined}
          isOpen={isOpenCargoForm}
          size={EFormDrawerSize.LARGE}
          displayMaskClickConfirmation
          onClose={handleCloseCargoForm}
          onFinish={handleCargoFinish}
        />
      )}
    </>
  );
};

export const CargoCreateWithOrder = withRoleGuard(CargoCreateWithOrderComponent, [
  UserRole.admin,
  UserRole.assistant,
  UserRole.customer_manager,
]);
